<template>
  <div
    v-if="visible"
    class="chip-standard"
    :class="classes"
    :tabindex="disabled ? -1 : tabindex || 0"
    :style="styles"
    @click="onClick"
    @keyup="e => isClickable ? onKeyup(e) : null"
  >
    <div
      v-if="isClickable"
      class="focus-helper"
    />
    <q-icon
      v-if="hasStartIcon"
      class="chip--icon chip--start-icon"
      :name="startIcon"
    />
    <div class="chip--content">
      <slot>
        <div
          v-if="label"
          class="chip--label"
        >
          {{ label }}
        </div>
      </slot>
    </div>
    <q-icon
      v-if="endIcon"
      class="chip--icon chip--end-icon"
      :name="endIcon"
    />
    <q-icon
      v-if="removable"
      class="chip--icon chip--remove-icon"
      role="button"
      aria-hidden="false"
      :aria-label="removeAriaLabel || $q.lang.label.remove"
      :name="removeIcon"
      :tabindex="disabled ? -1 : tabindex || 0"
      @click="onRemove"
      @keyup="onRemove"
    />
  </div>
</template>

<script setup>
import { computed } from 'vue'
import { useQuasar } from 'quasar'
import { cx } from '@/helpers'

defineOptions({ name: 'Chip' })
const visible = defineModel({ type: Boolean, default: true })
const selected = defineModel('selected', { type: Boolean, default: false })
const emit = defineEmits(['update:modelValue', 'remove', 'click'])
const props = defineProps({
  startIcon: { type: String, default: null },
  endIcon: { type: String, default: null },
  iconRemove: { type: String, default: null },
  iconSelected: { type: String, default: null },
  label: { type: [String, Number], default: null },
  size: {
    type: String,
    default: 'sm',
    validator: v => ['xs', 'sm', 'md', 'lg', 'xl'].includes(v)
  },
  color: {
    type: String,
    default: 'primary',
    validator: v => [
      'primary',
      'secondary',
      'info',
      'positive',
      'negative',
      'warning',
      'accent',
      'invert',
      'disabled'
    ].includes(v)
  },
  variant: {
    type: String,
    default: 'fade',
    validator: v => [
      'solid',
      'outline',
      'fade',
      'soft',
      'dashed'
    ].includes(v)
  },
  shape: {
    type: String,
    default: 'rounded',
    validator: v => ['square', 'rounded'].includes(v)
  },
  clickable: { type: Boolean, default: false },
  removable: { type: Boolean, default: false },
  removeAriaLabel: { type: String, default: null },
  tabindex: { type: [String, Number], default: null },
  disabled: { type: Boolean, default: false }
})

const $q = useQuasar()

const sizes = {
  xs: 16,
  sm: 20,
  md: 24,
  lg: 28,
  xl: 32
}

const hasStartIcon = computed(() => selected.value || props.startIcon)
const hasEndIcon = computed(() => props.removable || props.endIcon)
const startIcon = computed(() => selected.value
  ? props.iconSelected || 'mono-check'
  : props.startIcon)
const removeIcon = computed(() => props.iconRemove || 'mono-cross-circle-solid')
const isClickable = computed(() => !props.disabled && (props.clickable || selected.value !== null))
const classes = computed(() => cx(
  `size-${props.size}`,
  `color-${props.color}`,
  `variant-${props.variant}`,
  `shape-${props.shape}`,
  isClickable.value && 'is-clickable',
  props.disabled && 'is-disabled',
  selected.value && 'is-selected',
  hasStartIcon.value && 'has-start-icon',
  hasEndIcon.value && 'has-end-icon'
))
const styles = computed(() => ({
  '--chip-height': `${sizes[props.size]}px`
}))

const onKeyup = e => {
  if (e.keyCode === 13 /* ENTER */) onClick(e)
}

const onRemove = e => {
  if (e.keyCode === void 0 || e.keyCode === 13) {
    if (e.cancelable) e.preventDefault()
    e.stopPropagation()
    if (props.disabled === false) {
      emit('update:modelValue', false)
      emit('remove')
    }
  }
}

const onClick = e => {
  if (!props.disabled) {
    selected.value = !selected.value
    emit('click', e)
  }
}

</script>

<style scoped>
@reference "#app-styles.css";

.chip-standard {
  @apply relative inline-flex flex-nowrap items-center;
  @apply align-middle rounded-md outline-0 w-auto h-(--chip-height) max-w-full;

  .focus-helper {
    @apply absolute inset-0 pointer-events-none rounded-[inherit] opacity-0 outline-0;
    @apply transition-[background,opacity] duration-300 ease-out;
    &::before, &::after {
      @apply content-[''] absolute inset-0 opacity-0 rounded-[inherit];
      @apply transition-[background,opacity] duration-300 ease-out;
    }
    &::before { @apply bg-black; }
    &::after { @apply bg-white; }
  }
  &:focus > .focus-helper,
  &:hover > .focus-helper {
    @apply bg-current opacity-22;

    &:before { @apply opacity-10; }
    &:after { @apply opacity-40; }
  }

  &.variant-solid {
    &.color-primary   { @apply bg-primary-mid text-white; }
    &.color-secondary { @apply bg-secondary-mid text-white; }
    &.color-info      { @apply bg-info-mid text-white; }
    &.color-positive  { @apply bg-positive-mid text-white; }
    &.color-negative  { @apply bg-negative-mid text-white; }
    &.color-warning   { @apply bg-warning-mid text-white; }
    &.color-accent    { @apply bg-accent-mid text-white; }
    &.color-invert    { @apply layer-invert-base ink-invert-strong; }
    &.color-disabled  { @apply bg-secondary-fainter ink-disabled; }
  }
  &.variant-outline {
    @apply py-px border;

    &.color-primary   { @apply bg-primary-mid/15 text-primary-mid border-primary-mid/60; }
    &.color-secondary { @apply bg-secondary-mid/15 text-secondary-mid border-secondary-mid/60; }
    &.color-info      { @apply bg-info-mid/15 text-info-mid border-info-mid/60; }
    &.color-positive  { @apply bg-positive-mid/15 text-positive-mid border-positive-mid/60; }
    &.color-negative  { @apply bg-negative-mid/15 text-negative-mid border-negative-mid/60; }
    &.color-warning   { @apply bg-warning-mid/15 text-warning-mid border-warning-mid/60; }
    &.color-accent    { @apply bg-accent-mid/15 text-accent-mid border-accent-mid/60; }
    &.color-invert    { @apply bg-layer-invert-base/15 ink-invert-strong border-edge-invert-base/60; }
    &.color-disabled  { @apply bg-secondary-fainter/15 ink-disabled edge-secondary-fainter; }
  }
  &.variant-faint {
    &.color-primary   { @apply bg-primary-fainter text-primary-deeper; }
    &.color-secondary { @apply bg-secondary-fainter text-secondary-deeper; }
    &.color-info      { @apply bg-info-fainter text-info-deeper; }
    &.color-positive  { @apply bg-positive-fainter text-positive-deeper; }
    &.color-negative  { @apply bg-negative-fainter text-negative-deeper; }
    &.color-warning   { @apply bg-warning-fainter text-warning-deeper; }
    &.color-accent    { @apply bg-accent-fainter text-accent-deeper; }
    &.color-invert    { @apply bg-secondary-fainter ink-strong; }
    &.color-disabled  { @apply bg-secondary-fainter/60 ink-disabled; }
  }
  &.variant-fade {
    &.color-primary   { @apply bg-primary-mid/10 text-primary-deep }
    &.color-secondary { @apply bg-secondary-mid/10 text-secondary-deep; }
    &.color-info      { @apply bg-info-mid/10 text-info-deep; }
    &.color-positive  { @apply bg-positive-mid/10 text-positive-deep; }
    &.color-negative  { @apply bg-negative-mid/10 text-negative-deep; }
    &.color-warning   { @apply bg-warning-mid/10 text-warning-deep; }
    &.color-accent    { @apply bg-accent-mid/10 text-accent-deep; }
    &.color-invert    { @apply bg-layer-invert-base/10 ink-strong; }
    &.color-disabled  { @apply bg-secondary/10 ink-disabled; }
  }

  &.variant-dashed {
    @apply bg-transparent py-px border border-dashed;

    &.color-primary   { @apply text-primary-mid edge-primary-mid; }
    &.color-secondary { @apply text-secondary-mid edge-secondary-mid; }
    &.color-info      { @apply text-info-mid edge-info-mid; }
    &.color-positive  { @apply text-positive-mid edge-positive-mid; }
    &.color-negative  { @apply text-negative-mid edge-negative-mid; }
    &.color-warning   { @apply text-warning-mid edge-warning-mid; }
    &.color-accent    { @apply text-accent-mid edge-accent-mid; }
    &.color-invert    { @apply ink-invert-strong edge-invert-base; }
    &.color-disabled  { @apply ink-disabled edge-secondary-faint; }
  }

  &.shape-square { @apply rounded-sm; }
  &.shape-rounded { @apply rounded-full; }

  &.size-xs {
    @apply gap-1 text-[10px]! leading-3 px-1.5 py-0.5;
    &.has-start-icon { @apply pl-0.5; }
    &.has-end-icon { @apply pr-0.5; }
    .chip--content { @apply gap-0.5; }
    .chip--icon { @apply text-[12px] leading-3; }
  }
  &.size-sm {
    @apply gap-1.25 text-[12px]! leading-3.5 px-1.75 py-0.75;
    &.has-start-icon { @apply pl-0.75; }
    &.has-end-icon { @apply pr-0.75; }
    .chip--content { @apply gap-0.75; }
    .chip--icon { @apply text-[14px] leading-3.5; }
  }
  &.size-md {
    @apply gap-1.5 text-[14px]! leading-4 px-2 py-1;
    &.has-start-icon { @apply pl-1; }
    &.has-end-icon { @apply pr-1; }
    .chip--content { @apply gap-1; }
    .chip--icon { @apply text-[16px] leading-4; }
  }
  &.size-lg {
    @apply gap-1.75 text-[16px] leading-4.5 px-2.25 py-1.25;
    &.has-start-icon { @apply pl-1.25; }
    &.has-end-icon { @apply pr-1.25; }
    .chip--content { @apply gap-1.25; }
    .chip--icon { @apply text-[18px]! leading-4.5; }
  }
  &.size-xl {
    @apply gap-2 text-[18px] leading-5 px-3 py-1.5;
    &.has-start-icon { @apply pl-1.5; }
    &.has-end-icon { @apply pr-1.5; }
    .chip--content { @apply gap-1.5; }
    .chip--icon { @apply text-[20px]! leading-5; }
  }

  &.is-clickable { @apply cursor-pointer; }
  &.is-selected .q-avatar { @apply hidden; }

  .q-avatar { @apply text-(--chip-height) rounded-full; }
  .chip--remove-icon { @apply opacity-60 outline-0 cursor-pointer hover:opacity-100 focus:opacity-100; }
  .chip--content { @apply whitespace-nowrap select-none; }
}
</style>
